import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { authApi } from "@/lib/api";
import Login from "@/pages/Login";
import Schedule from "@/pages/Schedule";
import MobileSchedule from "@/pages/MobileSchedule";
import PlanningView from "@/pages/PlanningView";
import EquipmentView from "@/pages/EquipmentView";
import ProjectsPage from "@/pages/ProjectsPage";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { DialogHost } from "@/lib/dialog";

// Stable reference so <Toaster> doesn't get a new object on every render.
const TOASTER_OPTIONS = {
  style: {
    background: "#0A0A0A",
    border: "1px solid #2A2A2A",
    color: "#F5F5F5",
    fontFamily: "IBM Plex Sans, system-ui, sans-serif",
    fontSize: "12px",
    borderRadius: "2px",
  },
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = location.hash || window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = m[1];

    (async () => {
      try {
        const { user } = await authApi.exchangeSession(session_id);
        // CRITICAL: push the user into the auth context so ProtectedRoute sees it
        // immediately — otherwise we bounce back to /login while the context is still null.
        setUser(user);
        // Send freshly-authenticated users to the project picker. They can
        // pick (or create) a project, then navigate into the schedule.
        navigate("/projects", { replace: true });
      } catch (e) {
        console.error("Auth exchange failed", e);
        navigate("/login", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base text-ink-dim text-sm">
      <div className="mono">Authenticating…</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base text-ink-dim text-sm mono">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ResponsiveSchedule() {
  // Auto-switch to the mobile read-only view when the viewport is narrow.
  // Mobile users still get the desktop view at `/desktop` if they want it
  // (deep links carry over fine — same routes, same auth).
  const isMobile = useIsMobile();
  return isMobile ? <MobileSchedule /> : <Schedule />;
}

function PlanningViewWithNav() {
  const navigate = useNavigate();
  return <PlanningView onSwitchToSchedule={() => navigate("/")} />;
}

function EquipmentViewWithNav() {
  const navigate = useNavigate();
  return <EquipmentView onSwitchToSchedule={() => navigate("/")} />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle session_id during render before normal routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ResponsiveSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/m"
        element={
          <ProtectedRoute>
            <MobileSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/desktop"
        element={
          <ProtectedRoute>
            <Schedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan"
        element={
          <ProtectedRoute>
            <PlanningViewWithNav />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment"
        element={
          <ProtectedRoute>
            <EquipmentViewWithNav />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" toastOptions={TOASTER_OPTIONS} />
          <DialogHost />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
